/**
 * CompareSelector Component
 *
 * Subject selector button for comparison page.
 * Displays selected subject or empty state.
 * Opens dimension picker modal on click.
 *
 * Supports up to 4 variants (A, B, C, D) with different colors.
 *
 * Usage:
 * ```tsx
 * <CompareSelector
 *   variant="A"
 *   subject={subjectA}
 *   onClick={() => openPicker('A')}
 * />
 * ```
 */

import { ChevronDown } from 'lucide-react'
import { getDimensionConfig } from '@/config/comparisonDimensions'
import { getPlatformIcon } from '@/components/v11/shared/platformIcons'
import type { SubjectSelection } from '@/types/comparison'

interface CompareSelectorProps {
  /**
   * Variant: A (blue), B (purple), C (green), D (orange)
   */
  variant: 'A' | 'B' | 'C' | 'D'

  /**
   * Current subject selection
   */
  subject: SubjectSelection

  /**
   * Click handler to open dimension picker modal
   */
  onClick: () => void

  /**
   * Optional className
   */
  className?: string
}

/**
 * Get colors based on variant (A, B, C, D)
 */
function getVariantColors(variant: 'A' | 'B' | 'C' | 'D', hasSelection: boolean) {
  const colors = {
    A: {
      border: 'border-blue-500/30 hover:border-blue-500',
      bg: hasSelection ? 'bg-blue-500/10' : 'bg-card',
      text: 'text-blue-500',
      badge: 'bg-blue-500/20 text-blue-500',
    },
    B: {
      border: 'border-purple-500/30 hover:border-purple-500',
      bg: hasSelection ? 'bg-purple-500/10' : 'bg-card',
      text: 'text-purple-500',
      badge: 'bg-purple-500/20 text-purple-500',
    },
    C: {
      border: 'border-green-500/30 hover:border-green-500',
      bg: hasSelection ? 'bg-green-500/10' : 'bg-card',
      text: 'text-green-500',
      badge: 'bg-green-500/20 text-green-500',
    },
    D: {
      border: 'border-orange-500/30 hover:border-orange-500',
      bg: hasSelection ? 'bg-orange-500/10' : 'bg-card',
      text: 'text-orange-500',
      badge: 'bg-orange-500/20 text-orange-500',
    },
  }

  return colors[variant]
}

/**
 * Compare subject selector button.
 * Horizontal layout, compact design matching mockup.
 */
export function CompareSelector({
  variant,
  subject,
  onClick,
  className = '',
}: CompareSelectorProps) {
  const hasSelection = !!(subject.type && subject.item)

  const colors = getVariantColors(variant, hasSelection)
  const config = subject.type ? getDimensionConfig(subject.type) : null

  return (
    <button
      onClick={onClick}
      className={`
        compare-selector
        flex items-center gap-3 px-4 py-3 rounded-lg border border-v11-border
        transition-all cursor-pointer min-w-[220px]
        bg-v11-surface hover:bg-v11-surface-hover hover:border-v11-accent/50
        ${className}
      `}
      type="button"
    >
      {/* Badge */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className={`px-2 py-1 rounded text-xs font-bold ${colors.badge}`}>
          {variant}
        </div>
      </div>

      {/* Platform icon */}
      {hasSelection && subject.item?.platform && (() => {
        const pc = getPlatformIcon(subject.item.platform);
        return pc ? (
          <span className="flex-shrink-0 flex items-center justify-center" style={{ color: pc.color }} title={pc.name}>
            {pc.icon}
          </span>
        ) : null;
      })()}

      {/* Content */}
      <div className="flex-1 text-left min-w-0">
        {hasSelection && subject.item ? (
          <>
            <div className="text-sm font-semibold truncate text-v11-text-primary">
              {subject.item.name}
            </div>
            <div className="text-xs text-v11-text-muted truncate">
              {subject.item.subtitle}
            </div>
          </>
        ) : (
          <div className="text-sm text-v11-text-muted">
            Select Subject {variant}
          </div>
        )}
      </div>

      {/* Arrow */}
      <ChevronDown className="h-4 w-4 text-v11-text-muted flex-shrink-0" />
    </button>
  )
}

// Default export
export default CompareSelector
